/**
 * 宠物学习（TEACH-PAGES-117）的说法和归类：不碰视觉，只决定每种状态怎么说、放进哪一组、还能不能再点。
 * 依据：学习方案 PET-LEARNING-PLAN-V1（V1.1）第 6、9.1、9.3、16 节；adm1 TEACH-ORDER-QUOTA-117 的状态机。
 * 参考理解（P 的整理结果和七种回包）那一步的说法等路由挂上、接线时再加（README 记了 P 10-16 给的内容），这里不留没接上的东西。
 * 设计评审第一轮（10-16）：数字和量词不拆行、不许诺页面上没有的东西、额度用完时说清只是先记下想法。
 * 规矩：不写那几类内部词（禁用词表在用例里，不放产品代码：全树扫描会把字面当成界面文字）；失败是准备那一步的事，不说宠物学不会；
 * 次数只信服务端（不在前端加减）；没有可靠完成时间就不写倒计时；结果说不清时不给会建第二单的「再试一次」。
 * 数字和后面的量词之间用不断行空格（NBSP），窄屏上不会断成「1 / 次」。
 */
import type { TeachEntitlement, TeachLength, TeachOrder, TeachState, TeachTrigger } from "./teachTypes";

/** 数字连着量词：「2 次」不拆行。 */
export const n = (count: number, unit: string) => `${count} ${unit}`;

export const TEACH_ENTRY_TITLE = "TA 的小动作";
export const TEACH_ACTION = "教 TA 一个新动作";
export const TEACH_CONFIRM = "确认，教 TA 这个动作";
export const TEACH_REVISE = "再改一改";
/**
 * 引导（9.3 原句后半是「也可以给一张照片或一小段视频」）：上传参考的口子还没挂，这一版不许诺照片和视频，只请主人用文字说；
 * 上传接上以后换回 9.3 原句。
 */
export const TEACH_INVITE = "TA 有没有一个你特别熟悉的小动作？告诉我们 TA 是怎么做的。";
export const TEACH_SAVE_IDEA = "先记下想教 TA 的动作";
/** 次数用完时进描述页、存好以后：说清这只是先记下想法（9.3「保存教学想法」），不让人以为还能接着教。 */
export const SAVE_IDEA_LEAD = "这个账号的赠送机会用完了。先把想教 TA 的动作记下来，收费开放以后可以接着教。";
export const IDEA_SAVED = "想法记下了。收费开放以后，可以接着把它教给 TA。";

/** 列表里的分组：等你确认（唯一要马上动手的，放最前）、已学会、准备中、还没写完（只有发起的人看得到草稿）、没教成；其余不在列表里。 */
export type TeachGroup = "confirm" | "learned" | "in_progress" | "unfinished" | "ended" | "hidden";
export type ListedGroup = Exclude<TeachGroup, "hidden">;

const GROUP: Record<TeachState, TeachGroup> = {
  awaiting_confirm: "confirm",
  available: "learned",
  enabled: "learned",
  disabled: "learned",
  repreparing: "learned",
  queued: "in_progress",
  preparing: "in_progress",
  checking: "in_progress",
  result_pending: "in_progress",
  draft: "unfinished",
  failed: "ended",
  withdrawn: "ended",
  cancelled: "hidden",
  isolated: "hidden",
  deleted: "hidden",
};

export function teachGroup(state: TeachState): TeachGroup {
  return GROUP[state];
}

export const GROUP_ORDER: readonly ListedGroup[] = ["confirm", "learned", "in_progress", "unfinished", "ended"];
export const GROUP_TITLE: Record<ListedGroup, string> = {
  confirm: "等你确认",
  learned: "已学会",
  in_progress: "准备中",
  unfinished: "还没写完",
  ended: "这几次没准备好",
};

/** 按组排好：组内新的在前（updated_at 倒序）。 */
const waiting = (o: TeachOrder) => (o.state === "available" ? 1 : 0);

export function groupOrders(orders: readonly TeachOrder[]): { group: ListedGroup; orders: TeachOrder[] }[] {
  return GROUP_ORDER.map((group) => ({
    group,
    // 同组里「准备好了」（还等主人决定用不用）排最前，其余按更新时间新到旧（评审第二轮挑刺）
    orders: orders.filter((o) => teachGroup(o.state) === group).sort((a, b) => waiting(b) - waiting(a) || (a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0)),
  })).filter((g) => g.orders.length > 0);
}

/** 每种状态一个短标签和一句话。9.3 有的照 9.3；9.3 没写到的几种用平实的说法。 */
export const STATE_COPY: Record<Exclude<TeachState, "isolated" | "deleted">, { tag: string; line: string }> = {
  draft: { tag: "还没写完", line: "接着告诉我们 TA 是怎么做的。" },
  awaiting_confirm: { tag: "等你确认", line: "教学说明整理好了，看看对不对。" },
  queued: { tag: "准备中", line: "这次学习正在准备，TA 会照常生活。" },
  preparing: { tag: "准备中", line: "正在准备这个小动作，你可以先去逛逛。" },
  checking: { tag: "检查中", line: "正在检查动作是否自然、像 TA。" },
  result_pending: { tag: "核对中", line: "这次学习的结果还在核对中，无需重新提交。" },
  available: { tag: "准备好了", line: "新动作准备好了，看看像不像 TA。" },
  enabled: { tag: "可以用了", line: "TA 现在可以在合适的时候用这个动作了。" },
  disabled: { tag: "先不用了", line: "这个动作先不让 TA 用了，想让 TA 用的时候再打开。" },
  repreparing: { tag: "照新样子准备中", line: "TA 换了新样子，这个动作正照新样子重新准备，不占学习次数。" },
  failed: { tag: "没准备好", line: "这次没有准备好，没有扣除你的学习次数。" },
  withdrawn: { tag: "已退回次数", line: `这个动作没能准备好，已经退回 ${n(1, "次")}学习机会。` },
  cancelled: { tag: "已取消", line: "这次学习取消了，没有扣除你的学习次数。" },
};

export function stateCopy(state: TeachState): { tag: string; line: string } | null {
  return state === "isolated" || state === "deleted" ? null : STATE_COPY[state];
}

/** 列表行上的标签：和组名一样时不再重复（「准备中」组里的「准备中」）。 */
export function rowTag(state: TeachState): string | null {
  const copy = stateCopy(state);
  const group = teachGroup(state);
  if (!copy || group === "hidden") return null;
  return copy.tag === GROUP_TITLE[group] ? null : copy.tag;
}

/**
 * 学习成果页能预览：准备好了、可以用了、先不用了。照新样子准备中的旧动作不再算可用（adm1：重新准备期间回答「不可用」）。
 * 只按教学单的状态判断（A-2 10-16）：动作读口不看启用与否，停用以后三段也还在；隔离、删除的单页面不取片段。
 */
export function canPreview(state: TeachState): boolean {
  return state === "available" || state === "enabled" || state === "disabled";
}

/**
 * 预览卡的几种情形（6c2b 轻审高 2：原来读不出也一律说「在学」，已经能用的单也这么说）。
 * - 正在读：PREVIEW_LOADING；
 * - 读失败：PREVIEW_READ_FAILED，给「重新看看」；
 * - 读到了、三段没齐：「准备好了」的单照 A-2 说 TA 在学（刚准备好、还在生效）；已学会的单（可以用了、先不用了）不说在学，说这会儿看不到；都给「重新看看」。
 */
export const PREVIEW_LOADING = "正在看看 TA 学得怎么样…";
export const TEACH_STILL_LEARNING = "TA 在学新动作";

/** 「TA 的小动作」顶上的场景（TEACH-PAGES-VISUAL-117）下面那一句，也是这块场景的读屏说法 */
export const SCENE_PLAIN = (name: string) => `${name}在小窝里`;
export const SCENE_TAP = (name: string, title: string) => `${name}学会了「${title}」，点 TA 再做一遍`;
export const SCENE_AGAIN = (name: string, title: string) => `看${name}再做一遍「${title}」`;
export const SCENE_DOING = (name: string, title: string) => `${name}在做「${title}」`;
export const SCENE_ASLEEP = (name: string) => `${name}睡着了`;
export const SCENE_AWAY = (name: string) => `${name}这会儿出门了，不在小窝里`;
export const PREVIEW_READ_FAILED = "这会儿没读到 TA 的这个动作。";
export const PREVIEW_NOT_SHOWN = "这会儿看不到 TA 做一遍，学会的动作还在。";
export const PREVIEW_RETRY = "重新看看";
/** 准备好了、预览还没看成：先别让主人在没看过的情况下允许使用（设计评审第一轮）。读失败时说先重新看看。 */
export const PREVIEW_NOT_YET = "动作还在生效，稍后回来先看 TA 做一遍，再决定让不让 TA 用。";
export const PREVIEW_FAILED_WAIT = "先点「重新看看」，看过 TA 做一遍再决定让不让 TA 用。";

/**
 * 额度那一行（9.1：放在教学入口附近，按账号写，不在每只宠物上各写一个 3 次）。数字只取服务端给的。
 * - 用完：「你的 N 次赠送机会已用完。收费即将开放，已经学会的动作可以继续使用。」（9.3）——不出购买按钮、不提星币。
 * - 一次都没动过：「你的账号有 N 次赠送学习机会，可用于不同宠物。」（9.3 免费权益）
 * - 其余：「本账号剩余 N 次，可用于你的宠物」（9.1），有正在准备的另说一句。
 */
export function entitlementLine(e: TeachEntitlement): string {
  if (e.exhausted || e.available <= 0) return `你的 ${n(e.total, "次")}赠送机会已用完。收费即将开放，已经学会的动作可以继续使用。`;
  if (e.used === 0 && e.preparing === 0) return `你的账号有 ${n(e.total, "次")}赠送学习机会，可用于不同宠物。`;
  const base = `本账号剩余 ${n(e.available, "次")}，可用于你的宠物`;
  return e.preparing > 0 ? `${base}（有 ${n(e.preparing, "次")}正在准备）` : base;
}

/** 「我的」页入口那一行的小字：一行写得下的短说法（6c2b 轻审中 1），整句留给 /me/moves 顶上。数字照样只取服务端给的。 */
export function entitlementShort(e: TeachEntitlement): string {
  if (e.exhausted || e.available <= 0) return "赠送机会已用完";
  if (e.used === 0 && e.preparing === 0) return `有 ${n(e.total, "次")}赠送机会`;
  return `本账号剩余 ${n(e.available, "次")}`;
}

/** 还能不能教新的：只看服务端的 exhausted 和可用数；用完时入口换成「先记下想教 TA 的动作」。 */
export function canTeachNew(e: TeachEntitlement): boolean {
  return !e.exhausted && e.available > 0;
}

/** 教学说明里各项的说法（确认页、再改一改）。取值集合跟 adm1、C 10-16 定的一致。 */
export const LENGTH_LABEL: Record<TeachLength, string> = { once: "做一次就停", repeat_short: "连着做两三下", loop: "一直做，直到被打断" };
export const TRIGGER_LABEL: Record<TeachTrigger, string> = { self: "TA 自己想做的时候", owner_tap: "你点 TA 的时候" };
/** 做完回到哪个姿势（教学说明的 end_pose；A-2 的底姿只有这三种）。别的值不认、不写那一行 */
export const POSE_LABEL: Record<string, string> = { STAND: "站着", SIT: "坐着", LIE: "趴着" };
/** 确认页「更多说明」：教谁、气质、何时使用、参考、学会以后收在这里（可展开；方案第 6 节一项不少，I 10-16 定） */
export const CONFIRM_MORE = "更多说明";

export const UNNAMED = "还没起名的小动作";
/** 草稿在列表里那一行的小字：描述的头几个字，加引号（标题和详情页一样写「还没起名的小动作」，6c2b 轻审挑刺）。没写字就不给。 */
export function draftExcerpt(o: Pick<TeachOrder, "draft_text">): string | null {
  const draft = o.draft_text?.trim();
  if (!draft) return null;
  return `「${draft.length > 16 ? `${draft.slice(0, 16)}…` : draft}」`;
}
/** 这个小动作叫什么（列表和一单页面顶上同一个）：确认过的名字 → 整理出的名字 → 「还没起名的小动作」。不拿半句描述当名字（评审第一轮：像句子）。 */
export function headerTitle(o: Pick<TeachOrder, "confirmed_spec" | "spec">): string {
  return o.confirmed_spec?.title ?? o.spec?.title ?? UNNAMED;
}

/** 教学描述页 */
export const DESCRIBE_LABEL = "TA 是怎么做的";
export const DESCRIBE_EXAMPLE = "比如：看到我回家，会先歪一下头，再抬起右前爪。";
export const DESCRIBE_SAVE = "存下这段描述";
export const DESCRIBE_SAVE_IDEA = "记下这个想法";
export const DESCRIBE_MAX = 300;
export const DRAFT_SAVED = "描述存好了，想到什么随时回来改。";
/** 描述表单的键不能按时说为什么（6c2b 轻审挑刺）。 */
export const DESCRIBE_WHY_EMPTY = "先写几句 TA 是怎么做的，再存。";
export const DESCRIBE_WHY_SAME = "还没有改动。";
export const DRAFT_EDIT = "改好了";
/** 续写存好以后播报一句（评审第二轮高 2：原来存没存上读屏听不到）。 */
export const DRAFT_UPDATED = "改好了，已经存上。";

/** 不教了（取消，草稿和等确认时）：先问一句。 */
export const DROP = "不教这个了";
export const DROP_ASK = "不教这个了吗？写好的描述会一起清掉。";
export const DROP_YES = "不教了";
export const KEEP = "留着";

/** 教学确认页（方案第 6 节那张表，10 项）：次数、扣次规则、成果使用三句。次数照方案原文写当前可用，不在前端减一（评审第二轮中 2：次数只信服务端）。 */
export const CONFIRM_QUOTA = (available: number) => `用本账号 ${n(1, "次")}赠送机会，当前可用 ${n(available, "次")}`;
/** 次数用完时的确认页：这份说明先留着，收费开放以后再回来确认（评审第二轮挑刺：确认键灰了要有说法）。 */
export const CONFIRM_LATER = "赠送机会用完了，这份说明先留着，收费开放以后可以回来确认。";
export const CONFIRM_CHARGE = "准备好、可以用了才扣；明确没准备好不扣。";
export const CONFIRM_USE = "准备好后先看看，再决定让不让 TA 日常用；学会的动作不会因为次数用完而失效。";
export const CONFIRM_REFS = (count: number) => `你给的 ${n(count, "份")}参考，只用来看动作，不会公开。`;
/** 确认没收到回音（网络断、超时、服务端出错）：结果可能已经记下了；再点也沿用同一个键，不会重复扣（方案 9.3「结果待核」的意思）。 */
export const CONFIRM_UNKNOWN = "刚才没收到回音，确认可能已经记下了。先看看这一单现在的样子，再点一次也不会重复扣次数。";

/** 本次学习：三步，只亮当前那一步，不写时间；核对中不亮任何一步（不是哪一步，是结果在核）。 */
export const STAGES = ["准备", "检查", "好了"] as const;
export function stageIndex(state: TeachState): number {
  if (state === "result_pending") return -1;
  if (state === "checking") return 1;
  if (state === "available" || state === "enabled" || state === "disabled") return 2;
  return 0;
}
/** 「TA 的小动作」里 TA 和「的」之间用不断行空格：书名号里的名字不拆在两行（设计评审第二轮挑刺） */
export const LEARNING_NEXT = "准备好以后，这一页和「TA\u00a0的小动作」里都会显示出来。";
export const BACK_TO_LIFE = "回小窝看看 TA";
export const BACK_TO_MAP = "去地图上看看 TA";

/** 学习成果 */
export const PREVIEW_PLAY = "看 TA 做一遍";
export const ENABLE_FIRST = "允许 TA 在合适的时候用";
export const ENABLE_AGAIN = "让 TA 再用起来";
export const DISABLE = "先不让 TA 用";
export const DELETE = "删掉这个动作";
export const DELETE_WARNING = "删掉以后，TA 就不会再用这个动作了。删掉不会退回学习次数，以后想再教，要重新用一次。";
export const DELETE_YES = "删掉";
export const GONE = "这个小动作已经不在了。";

/** 没准备好：失败是准备那一步的事，不是宠物学不会（方案 16 节）。 */
export const ENDED_REASSURE = "问题出在准备这一步，不是 TA 学不会。";
export const BACK_TO_LIST = "回到 TA 的小动作";

/**
 * 参考照片（TEACH-UNDERSTAND-UI-117，adm1 TEACH-WIRE-117）：只在草稿页上传，每单最多 3 张，能删；视频帧进 118。
 * 上传前那一句照实说用处和去向：原图在这一单教完（能用了，或者没教成、取消）以后整单删掉（adm1 HTTP-CONTRACT 第一节）。
 */
export const REFS_TITLE = "参考照片";
export const REFS_LEAD = "有拍到 TA 做这个动作的照片，可以放上来，TA 学得更像。";
export const REFS_PRIVACY = "照片只用来教 TA 这个动作，不会公开；教完就删掉，你也可以先删。";
export const REFS_MAX = 3;
export const REFS_ADD = "放一张照片";
export const REFS_FULL = `最多 ${n(REFS_MAX, "张")}，已经放满了。`;
export const REFS_ACCEPT = "image/jpeg,image/png,image/webp";
export const REFS_BAD_TYPE = "只能放 JPG、PNG 或 WebP 照片。";
export const REFS_TOO_LARGE = "这张照片太大了，换一张小一点的。";
export const REFS_CONFIRMED = "这一单已经确认过，不能再改照片了。";
export const REFS_ADDED = "照片放好了。描述和照片都改了，要重新整理一下。";
export const REFS_REMOVED = "删掉了。";
export const refsPhoto = (index: number) => `参考照片 ${index}`;
export const refsRemove = (index: number) => `删掉参考照片 ${index}`;

/**
 * 整理成教学说明（adm1 TEACH-WIRE-117 HTTP-CONTRACT 第二节）：按回来的结果各说各的，不写「生成」、AI、模型；
 * 结果说不清（unknown）不重试、也不给按钮；整理没装（503 unavailable）、今天的次数到了（429）也不给按钮（I 10-17）。
 * 这里的次数是平台的整理次数，不是主人的 3 次赠送学习机会，不提收费。
 */
export const UNDERSTAND_TITLE = "整理成教学说明";
export const UNDERSTAND_LEAD = "写好了就整理一下：把描述和照片整理成一份教学说明，你看过、确认了才开始教。";
export const UNDERSTAND_GO = "整理一下";
export const UNDERSTAND_WHY_EMPTY = "先写几句 TA 是怎么做的，再整理。";
export const UNDERSTAND_WHY_DIRTY = "描述改过还没存，先点「改好了」，再整理。";
export const UNDERSTAND_ASK = "还想问你一两句，答了就接着整理：";
export const ANSWER_SEND = "回答";
export const ANSWER_MAX = 60;
export const ANSWER_WHY_EMPTY = "每个问题都写一句再回答。";
export const UNDERSTAND_SPLIT = "这一段里有好几个动作，每个要各用 1 次学习机会。先挑一个教，别的可以另存成草稿：";
export const SPLIT_PICK = "先教这个";
export const SPLIT_SAVE = "另存成草稿";
export const SPLIT_SAVED = "另存好了，在「还没写完」里。";
export const splitPicked = (name: string) => `描述换成了「${name}」，再整理一下。`;
export const UNDERSTAND_NOT_SUPPORTED = "这一版里有的地方，TA 现在还学不了：";
export const UNDERSTAND_SIMPLER = "可以先教简单一点的：";
export const SIMPLER_USE = "照这个改描述";
export const UNDERSTAND_UNUSABLE = "这一版没整理出来。写清楚 TA 先做什么、再做什么，改好再整理一次。";
export const UNDERSTAND_UNKNOWN = "这次整理的结果还在核对，不用重新提交。";
export const UNDERSTAND_IN_FLIGHT = "正在整理这一版，过几秒再看看。";
export const UNDERSTAND_CHANGED = "描述刚改过，已经换成最新的一版，再点一次「整理一下」。";
export const UNDERSTAND_NOT_SENT = "这次没发出去，可以再试一次。";
export const UNDERSTAND_RETRY = "再试一次";
export const UNDERSTAND_UNAVAILABLE = "现在还整理不了，过一会儿再来。";
export const UNDERSTAND_DAILY = "今天先到这里，明天再整理。";
export const UNDERSTAND_NO_QUESTION = "这个问题已经答过了，再点一次「整理一下」看看。";
/** 一个动作的样子写成一段描述（拆分、简化以后换进草稿）：名字加几步，最多 300 字 */
export const sketchText = (sketch: { name: string; steps: readonly string[] }) => `${sketch.name}：${sketch.steps.join("，")}。`.slice(0, DESCRIBE_MAX);
